import React, { useState } from 'react';
import { ApiClientDetails } from '../../apis';
import { useCreateApiClient, useDeleteApiClient, useQueryApiClients } from './apiClientActions';
import {
  SalvBanner,
  SalvBannerAccent,
  SalvBannerType,
  SalvButton,
  SalvLabel,
  SalvTable,
  Size,
} from '@salvapp/ui-components';
import { createColumnHelper } from '@tanstack/react-table';
import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import { copyToClipboard } from '../../common/clipboard';
import ApiClientCreatedModal from './ApiClientCreatedModal';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import ExternalLink from '../../common/ExternalLink';

const API_DOCS_AUTHENTICATION_LINK = 'https://docs.salv.com/api/#tag/getting-started/Authentication';

const ApiDocumentationLink = () => (
  <div style={{ whiteSpace: 'nowrap' }}>
    <ExternalLink title='See the API documentation' link={API_DOCS_AUTHENTICATION_LINK} />
  </div>
);

const LegacyCredentialsSunsetWarning = () => (
  <SalvBanner
    type={SalvBannerType.WARNING}
    accent={SalvBannerAccent.LIGHT}
    message='Credentials marked Legacy still issue long-lived access tokens and stop working on 10 November 2026. Create new credentials and migrate integrations before then.'
  >
    <ApiDocumentationLink />
  </SalvBanner>
);

const AccessTokenLifetimeNote = () => (
  <div className='text-s'>
    Access tokens for these credentials are short-lived. Integrations must read{' '}
    <span className='code-s'>expires_in</span> from the token response and request a new token before the current one
    expires. <ExternalLink title='See the API documentation' link={API_DOCS_AUTHENTICATION_LINK} />
  </div>
);

const isLegacyClient = (client: ApiClientDetails) => client.longLivedAccessTokens === true;

const columnHelper = createColumnHelper<ApiClientDetails>();

const ApiSecret = () => {
  const dispatch = useDispatch();
  const [createdClient, setCreatedClient] = useState<ApiClientDetails>();
  const { data: clients = [], isPending, isFetching } = useQueryApiClients();
  const createApiClient = useCreateApiClient();
  const deleteApiClient = useDeleteApiClient();

  const createClientCredentials = async () => {
    setCreatedClient(await createApiClient.mutateAsync());
  };

  const copyValue = async (label: string, value: string) => {
    const copied = await copyToClipboard(value);
    dispatch(
      showFeedback(
        copied
          ? { message: `${label} copied`, success: true }
          : { message: 'Could not copy to clipboard', success: false }
      )
    );
  };

  const deleteClientCredentials = async (client: ApiClientDetails) => {
    dispatch(
      showConfirmationModal({
        title: 'Delete credentials?',
        description: (
          <>
            Integrations using <span className='code-s'>{client.clientId}</span> stop authenticating immediately.
          </>
        ),
        confirmationText: 'Delete',
        confirmButtonVariant: 'danger',
        cancelText: 'Cancel',
        onConfirm: async () => {
          await deleteApiClient.mutateAsync(client.clientId);
          dispatch(showFeedback({ message: 'Deleted', success: true }));
        },
      })
    );
  };

  const columns = [
    columnHelper.accessor('clientId', {
      header: 'Client id',
      size: 45,
      cell: ({ getValue }) => <span className='code-s'>{getValue()}</span>,
    }),
    columnHelper.accessor('createdTime', {
      header: 'Created',
      size: 20,
      cell: ({ getValue }) => <FormattedDateTime date={getValue()} showTime />,
    }),
    columnHelper.accessor('longLivedAccessTokens', {
      header: 'Status',
      size: 15,
      cell: ({ row }) =>
        isLegacyClient(row.original) ? (
          <SalvLabel variant='warning'>Legacy</SalvLabel>
        ) : (
          <SalvLabel variant='secondary'>Current</SalvLabel>
        ),
    }),
    columnHelper.display({
      id: 'actions',
      size: 20,
      cell: ({ row }) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SalvButton
            label='Delete'
            icon={faTrash}
            variant='danger-outline'
            size={Size.SMALL}
            onClick={() => deleteClientCredentials(row.original)}
          />
        </div>
      ),
    }),
  ];

  const hasLegacyClients = clients.some(isLegacyClient);

  return (
    <>
      {hasLegacyClients && (
        <div className='m-b-16'>
          <LegacyCredentialsSunsetWarning />
        </div>
      )}

      <SalvTable
        title='API client credentials'
        data={clients}
        columns={columns}
        striped
        ariaLabel='API client credentials'
        emptyMessage='No API client credentials'
        className='m-b-24'
        loadingStatus={{ isPending, isFetching }}
        actions={
          <SalvButton
            label='New credentials'
            icon={faPlus}
            variant='primary'
            size={Size.SMALL}
            disabled={createApiClient.isPending}
            onClick={() => createClientCredentials()}
          />
        }
      />

      {createdClient && (
        <ApiClientCreatedModal client={createdClient} onHide={() => setCreatedClient(undefined)} onCopy={copyValue}>
          <AccessTokenLifetimeNote />
        </ApiClientCreatedModal>
      )}
    </>
  );
};

export default ApiSecret;
